import { useState } from 'react'
import { CircularProgress, DialogActions, DialogContent, Typography } from '@mui/material'
import { useDispatch } from 'react-redux'
import { DarkButton } from '@components'
import { showSnackbar } from '@global/snackbar/snackbar.actions'
import { useApprovalContext } from '@templates/approval/contexts'
import { ActionType } from '@templates/approval/contexts/approvalContext/types'
import { api } from 'api'
import * as css from './styles'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

interface Props {
  handleClose: () => void
}

export const ConfirmAcceptDialogContent = ({ handleClose }: Props) => {
  const reduxDispatch = useDispatch()
  const { dispatch, showApprovalRequestsTable, reviewingRequest } = useApprovalContext() ?? {
    dispatch: () => void {},
    showApprovalRequestsTable: () => void {},
    reviewingRequest: null,
  }
  const [accepting, setAccepting] = useState(false)
  const numOfRows = reviewingRequest?.rows ?? 0
  const rowText = numOfRows > 1 ? 'rows' : 'row'

  return (
    <>
      <DialogContent css={css.content}>
        <Typography variant="body1">
          <Trans>
            You are about to save changes to&nbsp;
            <strong>{numOfRows}</strong>
            &nbsp;
            {rowText} of data
          </Trans>
        </Typography>
      </DialogContent>
      <DialogActions>
        <DarkButton onClick={handleClose}>{t`Cancel`}</DarkButton>
        <css.ButtonContainer>
          <DarkButton onClick={handleConfirm} disabled={accepting} contained>
            {t`Accept & save changes`}
          </DarkButton>
          {accepting && <CircularProgress size={20} css={css.buttonProgress} />}
        </css.ButtonContainer>
      </DialogActions>
    </>
  )

  async function handleConfirm() {
    setAccepting(true)

    try {
      await approveSubmission()
      await resumeSubmission()
      setAccepting(false)
      handleClose()
      dispatch({
        type: ActionType.setReviewingRequest,
        payload: {
          reviewingRequest: null,
          reloadApprovalRequests: true,
        },
      })
      reduxDispatch(
        showSnackbar({
          message: t`Submission approved successfully`,
          type: 'SUCCESS',
          duration: 5000,
        }),
      )
      showApprovalRequestsTable()
    } catch {
      setAccepting(false)
      handleClose()
      reduxDispatch(
        showSnackbar({
          message: t`Failed to approve submission`,
          type: 'ERROR',
          duration: 5000,
        }),
      )
    }
  }

  function approveSubmission() {
    return api.put(
      `/api/v1/account/dataset/submission/${String(reviewingRequest?.submission_id ?? -1)}/approve`,
    )
  }

  function resumeSubmission() {
    return api.put(
      `/api/v1/account/dataset/submission/${String(reviewingRequest?.submission_id ?? -1)}/resume`,
    )
  }
}
